/**
 * Reads the platform's identity claims from an authenticated OIDC user.
 *
 * The Cognito Pre-Token-Generation trigger promotes `custom:accountId` / `custom:role` to top-level
 * `accountId` / `role` JWT claims (see iac cognito-identity), so they appear on the OIDC user's
 * `profile`. `sub` needs no trigger — it is the standard OIDC subject claim. The header displays the
 * first two and the members list uses the third to mark the reader's own row; every data read is
 * still scoped server-side from the same token, so all three are for display only.
 */

import type { User } from 'oidc-client-ts';

export interface Identity {
  accountId?: string;
  role?: string;
  /**
   * The signed-in user's Cognito `sub` — the standard OIDC subject claim, not a custom one.
   *
   * A `MemberDto` identifies a member by `sub` and nothing else (Req 2.3 keeps email and name in
   * Cognito), so this is the only way the members list can tell the reader which opaque identifier
   * is theirs. Display only, like the other two: the API derives the acting principal from the same
   * token (Req 1.6 forbids taking a user identifier from request input).
   */
  userSub?: string;
  /**
   * The signed-in user's display name — the standard OIDC `name` claim, absent when it was never set.
   *
   * Read from the token rather than from `GET /v1/me` because the shell's user panel renders on every
   * page: fetching a profile to label it would add a request to every navigation to show something the
   * token already carries. The profile page still reads the API, which is the authority — this is a
   * label, and a stale one only lasts until the next token renew.
   */
  name?: string;
}

function claimString(profile: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = profile?.[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Extract the `accountId` / `role` / `sub` / `name` claims from an authenticated user (all optional). */
export function identityFromUser(user: User | null | undefined): Identity {
  const profile = user?.profile as Record<string, unknown> | undefined;
  return {
    accountId: claimString(profile, 'accountId'),
    role: claimString(profile, 'role'),
    userSub: claimString(profile, 'sub'),
    name: claimString(profile, 'name'),
  };
}
